import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from './button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

/**
 * ThemeToggle - Toggle button for switching between light and dark themes
 * Displays sun icon in dark mode and moon icon in light mode
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  // Prevent hydration mismatch by only rendering after mount
  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <Button
        variant="outline"
        size="icon"
        className="flex h-9 w-9 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center p-0 lg:h-8 lg:w-8 lg:min-h-8 lg:min-w-8"
        aria-label="Toggle theme"
        disabled
      >
        <Sun className="h-4 w-4 text-gray-600" aria-hidden="true" />
      </Button>
    );
  }

  const isDark = theme === 'dark';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="flex h-9 w-9 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center p-0 transition-all duration-300 ease-in-out dark:border-gray-700 dark:hover:bg-gray-800 lg:h-8 lg:w-8 lg:min-h-8 lg:min-w-8"
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <div className="relative w-4 h-4">
              <Sun 
                aria-hidden="true"
                className={`h-4 w-4 absolute inset-0 transition-all duration-300 ease-in-out ${
                  isDark 
                    ? 'opacity-100 rotate-0 text-gray-300' 
                    : 'opacity-0 rotate-90 text-gray-600'
                }`}
              />
              <Moon 
                aria-hidden="true"
                className={`h-4 w-4 absolute inset-0 transition-all duration-300 ease-in-out ${
                  isDark 
                    ? 'opacity-0 -rotate-90 text-gray-300' 
                    : 'opacity-100 rotate-0 text-gray-600'
                }`}
              />
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent sideOffset={11} className="dark:bg-gray-800 dark:border-gray-700 dark:text-white z-[102]">
          <p>{isDark ? 'Switch to light mode' : 'Switch to dark mode'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

